/**
 * Wallet storage operations for Cashero.
 *
 * CRUD helpers for wallet-side IndexedDB data using Dexie.
 */

import { db, type WalletMintEntry, type StoredProof, type TransactionRecord, type PendingOperation } from './db';
import type { Proof } from '@/lib/cashu/proof';

// ====================================================================
// Wallet Preferences
// ====================================================================

/** Get wallet display unit preference */
export async function getDisplayUnit(): Promise<'xmr' | 'piconero'> {
  const config = await db.walletConfig.get('display');
  return (config?.displayUnit as 'xmr' | 'piconero') ?? 'xmr';
}

/** Set wallet display unit preference */
export async function setDisplayUnit(unit: 'xmr' | 'piconero'): Promise<void> {
  await db.walletConfig.put({ id: 'display', displayUnit: unit, updatedAt: Date.now() });
}

// ====================================================================
// Mints
// ====================================================================

/** Get all mints in the wallet */
export async function getAllMints(): Promise<WalletMintEntry[]> {
  return db.walletMints.toArray();
}

/** Get a specific mint by npub */
export async function getMint(npub: string): Promise<WalletMintEntry | undefined> {
  return db.walletMints.get(npub);
}

/** Add a mint to the wallet */
export async function addMint(mint: WalletMintEntry): Promise<void> {
  await db.walletMints.put(mint);
}

/** Remove a mint from the wallet */
export async function removeMint(npub: string): Promise<void> {
  await db.walletMints.delete(npub);
  // Also remove all proofs for this mint
  await db.walletProofs.where('mintNpub').equals(npub).delete();
}

/** Update mint keysets (after refresh) */
export async function updateMintKeysets(npub: string, keysets: WalletMintEntry['keysets']): Promise<void> {
  const mint = await db.walletMints.get(npub);
  if (mint) {
    mint.keysets = keysets;
    await db.walletMints.put(mint);
  }
}

// ====================================================================
// Proofs
// ====================================================================

/** Get all proofs for a specific mint */
export async function getProofsForMint(mintNpub: string): Promise<StoredProof[]> {
  return db.walletProofs.where('mintNpub').equals(mintNpub).toArray();
}

/** Get all proofs across all mints */
export async function getAllProofs(): Promise<StoredProof[]> {
  return db.walletProofs.toArray();
}

/** Add a batch of proofs */
export async function addProofs(proofs: StoredProof[]): Promise<void> {
  await db.walletProofs.bulkAdd(proofs);
}

/** Remove proofs by their secrets */
export async function removeProofsBySecrets(secrets: string[]): Promise<void> {
  await db.walletProofs.where('secret').anyOf(secrets).delete();
}

/** Get balance for a specific mint */
export async function getMintBalance(mintNpub: string): Promise<number> {
  const proofs = await getProofsForMint(mintNpub);
  return proofs.reduce((sum, p) => sum + p.amount, 0);
}

/** Get total balance across all mints */
export async function getTotalBalance(): Promise<number> {
  const proofs = await getAllProofs();
  return proofs.reduce((sum, p) => sum + p.amount, 0);
}

// ====================================================================
// Transaction History
// ====================================================================

/** Add a transaction record */
export async function addTransaction(tx: TransactionRecord): Promise<void> {
  await db.walletHistory.put(tx);
}

/** Update a transaction's status */
export async function updateTransactionStatus(id: string, status: TransactionRecord['status'], updates?: Partial<TransactionRecord>): Promise<void> {
  const existing = await db.walletHistory.get(id);
  if (existing) {
    await db.walletHistory.put({ ...existing, ...updates, status });
  }
}

/** Get transaction history (newest first) */
export async function getTransactionHistory(limit: number = 50, mintNpub?: string): Promise<TransactionRecord[]> {
  let query = db.walletHistory.orderBy('timestamp').reverse();
  if (mintNpub) {
    query = query.filter(tx => tx.mintNpub === mintNpub) as any;
  }
  return query.limit(limit).toArray();
}

// ====================================================================
// Pending Operations
// ====================================================================

/** Save a pending operation */
export async function addPendingOp(op: PendingOperation): Promise<void> {
  await db.walletPendingOps.put(op);
}

/** Update pending op status */
export async function updatePendingOpStatus(requestId: string, status: PendingOperation['status']): Promise<void> {
  const op = await db.walletPendingOps.get(requestId);
  if (op) {
    op.status = status;
    await db.walletPendingOps.put(op);
  }
}

/** Get all pending operations for a mint */
export async function getPendingOpsForMint(mintNpub: string): Promise<PendingOperation[]> {
  return db.walletPendingOps.where('mintNpub').equals(mintNpub).toArray();
}

/** Clear completed/failed operations older than 1 hour */
export async function cleanupOldPendingOps(): Promise<void> {
  const cutoff = Date.now() - 3600000;
  const old = await db.walletPendingOps
    .filter(op => op.createdAt < cutoff && (op.status === 'completed' || op.status === 'failed'))
    .toArray();

  for (const op of old) {
    await db.walletPendingOps.delete(op.requestId);
  }
}
